import { infuse } from '../ioc-config';
import { IDealerComplete } from '../models/dealer.model';
import { Logger } from '../utils/logger.util';

export const OUTLET_ID_PARAM = 'gsOutletId';
export const ALL_OUTLET_IDS_PARAM = 'allGsOutletIds';

export const initializeDealerContext = async (url: URL = new URL(window.location.href)): Promise<void> => {
  try {
    const { dealerContextDispatchers, getDealerContextState } = await import(
      '../stores/dealerContext/dealerContext.connection'
    );
    const gsOutletIdParam = url.searchParams.get(OUTLET_ID_PARAM);
    const gsOutletIdRegex = /GS\d+/;
    const isValidOutletIdParam = !!gsOutletIdParam && gsOutletIdRegex.test(gsOutletIdParam);

    if (isValidOutletIdParam) {
      // check if its the same dealer as in our own persisted store
      const dealerContextState = await getDealerContextState();

      if (gsOutletIdParam === dealerContextState.DealerContext?.outletId) {
        return;
      }

      // Call DLC API
      const dealerApi = infuse('dealerApi');
      const { dealerLocator, lang } = infuse('config');
      let outlet: IDealerComplete;

      // if dealerLocator.languagesForLocalizedData then get the dealersById with localization
      if (dealerLocator.languagesForLocalizedData) {
        const [_outlet] = await dealerApi.getDealersById(
          gsOutletIdParam,
          dealerLocator.languagesForLocalizedData.includes(lang),
          ['*'],
        );

        outlet = _outlet;
      } else {
        const [_outlet] = await dealerApi.getDealersById(gsOutletIdParam);

        outlet = _outlet;
      }

      if (!outlet) {
        // invalid/non-existant outlet id, do nothing
        return;
      }

      const allGsOutletIdsParam = url.searchParams.get(ALL_OUTLET_IDS_PARAM);

      const businessNames = [
        ...new Set(outlet.brands.filter((info) => info.brand?.code === 'MB').map((brand) => brand.businessName)),
      ];
      const legalName = outlet.baseInfo.name1;

      let name: string;

      if (dealerLocator.enforceLegalName || businessNames.length === 0) {
        name = legalName;
      } else {
        name = businessNames[0];
      }

      // store dealer context in store
      dealerContextDispatchers.setDealerContext({
        legalName: name,
        dealerOrgName: name,
        outletId: outlet.baseInfo.externalId,
        companyId: outlet.baseInfo.companyId,
        legacyId: outlet.baseInfo.legacyId,
        email: outlet.brands?.[0]?.contactDetails?.email,
        phone: outlet.brands?.[0]?.contactDetails?.phone,
        website: outlet.brands?.[0]?.contactDetails?.website,
        allOutletId: allGsOutletIdsParam || gsOutletIdParam,
      });
    } else {
      // Check if we have a persisted dealer context in our store
      const dealerContextState = await getDealerContextState();

      // We have a dealer context, add parameters to current URL
      if (dealerContextState.DealerContext) {
        const router = infuse('router');

        router.replace({
          globalQuery: {
            [OUTLET_ID_PARAM]: dealerContextState.DealerContext.outletId,
            [ALL_OUTLET_IDS_PARAM]: dealerContextState.DealerContext.allOutletId,
          },
        });
      }
    }
  } catch (error) {
    Logger.error('Failed to initialize dealer context', error);
  }
};
